export type ExportRow = Record<string, unknown>;
/** An Excel-native cell, so money, percentages and dates still sum and sort in the sheet. */
export interface ExportExcelCell { v: number; z: string }

/** Excel's day number for a calendar day (dates are written as numbers with a date format). */
export function excelDay(year: number, monthIndex: number, day: number): number {
  return (Date.UTC(year, monthIndex, day) - Date.UTC(1899, 11, 30)) / 86_400_000;
}
export interface ExportColumn {
  key: string;
  label: string;
  visible: boolean;
  /** Row fields this column reads (its label, term structure, link id). Defaults to the key. */
  fields?: string[];
  /** The text the grid shows for this row (REG-874). Without it the raw value is written. */
  text?: (row: ExportRow) => string;
  /** The same value as a typed Excel cell; text is used when absent or null. */
  excel?: (row: ExportRow) => ExportExcelCell | null;
  /** A record link for this cell in Excel. */
  href?: (row: ExportRow) => string | null;
}

/** Every row field the chosen columns need, in column order. */
export function exportColumnFields(columns: ExportColumn[]): string[] {
  return [...new Set(columns.flatMap((column) => column.fields ?? [column.key]))];
}
export interface ExportPage {
  rows: ExportRow[];
  total: number | null;
  /** Shown with the result, e.g. when the source caps how many rows it returns. */
  notice?: string;
}
export interface ExportPageRequest {
  offset: number;
  after: number | string | null;
  limit: number;
  signal: AbortSignal;
  fields: string[];
  selectedIds?: (number | string)[];
}
export type ExportPageLoader = (request: ExportPageRequest) => Promise<ExportPage>;

/** The order of the file's rows: the row fields it reads and the sort itself. */
export interface ExportRowOrder {
  fields: string[];
  sort: (rows: ExportRow[]) => ExportRow[];
}

export function describeExportFilters(filters: Record<string, unknown>, labels: Record<string, string>): string {
  const operators: Record<string, string> = { equals: 'is', notEqual: 'is not', contains: 'contains', startsWith: 'starts with',
    greaterThan: 'after / greater than', greaterThanOrEqual: 'on or after / at least', lessThan: 'before / less than',
    lessThanOrEqual: 'on or before / at most', blank: 'is empty', notBlank: 'is not empty', plain_fts: 'matches search', inRange: 'between' };
  const describe = (value: unknown, key: string): string => {
    if (!value || typeof value !== 'object') return '';
    const filter = value as Record<string, unknown>;
    if (Array.isArray(filter.conditions)) return `(${filter.conditions.map((condition) => describe(condition, key)).join(` ${filter.type === 'OR' || filter.operator === 'OR' ? 'or' : 'and'} `)})`;
    const field = String(filter.colId ?? key);
    const label = labels[field] ?? labels[field.replace(/_rel->>label$/, '')] ?? field.replace(/_/g, ' ');
    if (Array.isArray(filter.values)) return `${label} is one of: ${filter.values.join(', ') || '(none)'}`;
    const raw = filter.filter ?? filter.dateFrom ?? '';
    const text = typeof raw === 'boolean' ? raw ? 'Yes' : 'No' : String(raw);
    return `${label} ${operators[String(filter.type)] ?? String(filter.type ?? 'is')} ${text}${filter.filterTo ?? filter.dateTo ? ` to ${filter.filterTo ?? filter.dateTo}` : ''}`.trim();
  };
  return Object.entries(filters).map(([key, value]) => describe(value, key)).filter(Boolean).join('; ') || 'No filters. All records you can view will be included.';
}

/** A short page is not EOF: PostgREST can impose a smaller server row limit. */
export async function collectExportRows(
  load: ExportPageLoader,
  signal: AbortSignal,
  progress: (loaded: number, total: number | null, notice?: string) => void,
  rowKey?: string,
  fields: string[] = [],
  selectedIds?: (number | string)[],
): Promise<ExportRow[]> {
  const rows: ExportRow[] = [];
  const seen = new Set<unknown>();
  let total: number | null = null;
  let after: number | string | null = null;
  let notice: string | undefined;
  while (true) {
    signal.throwIfAborted();
    const limit = selectedIds ? 100 : 500;
    const page = await load({ offset: rows.length, after, limit, signal, fields,
      selectedIds: selectedIds?.slice(rows.length, rows.length + limit) });
    signal.throwIfAborted();
    if (rows.length === 0) total = selectedIds?.length ?? page.total;
    if (page.notice) notice = page.notice;
    if (!page.rows.length) {
      if (total !== null && rows.length !== total) throw new Error('The records changed during export. Please try again.');
      break;
    }
    for (const row of page.rows) {
      if (rowKey) {
        const id = row[rowKey];
        if (id == null || seen.has(id)) throw new Error('Export stopped because a record was missing an ID or appeared twice. Please try again.');
        seen.add(id);
      }
      rows.push(row);
    }
    if (rowKey) after = page.rows[page.rows.length - 1][rowKey] as number | string;
    progress(rows.length, total, notice);
    if (total !== null && rows.length >= total) {
      if (rows.length !== total) throw new Error('The records changed during export. Please try again.');
      break;
    }
  }
  return rows;
}

export function exportCellValue(row: ExportRow, key: string): string | number | boolean {
  const related = row[`${key}_rel`];
  const value = related && typeof related === 'object' && 'label' in related
    ? related.label
    : key.split('.').reduce<unknown>((v, part) => v && typeof v === 'object' ? (v as ExportRow)[part] : undefined, row);
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'object' && 'label' in value) return String(value.label ?? '');
  return JSON.stringify(value);
}

export function csvCell(value: string | number | boolean): string {
  // Quoting alone does not stop spreadsheet formula interpretation.
  const text = typeof value === 'string' && /^[\s]*[=+@-]/.test(value) ? `'${value}` : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

export function exportFilename(name: string, format: 'csv' | 'xlsx'): string {
  const base = name.replace(/\.(csv|xlsx)$/i, '').split('').filter((char) => char.charCodeAt(0) >= 32).join('').replace(/[<>:"/\\|?*]/g, '-').trim().slice(0, 120);
  return `${base || 'crm-export'}.${format}`;
}

export async function createExportBlob(
  rows: ExportRow[], columns: ExportColumn[], format: 'csv' | 'xlsx',
): Promise<Blob> {
  const header = columns.map((column) => column.label);
  const text = (row: ExportRow, column: ExportColumn) => column.text ? column.text(row) : exportCellValue(row, column.key);
  if (format === 'csv') {
    const values = rows.map((row) => columns.map((column) => text(row, column)));
    return new Blob(['\uFEFF', [header, ...values].map((row) => row.map(csvCell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8' });
  }
  if (rows.length > 1_048_575) throw new Error('This export exceeds the Excel row limit. Choose CSV or narrow your filters.');
  const XLSX = await import('xlsx');
  const book = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet([header, ...rows.map((row) => columns.map((column) => text(row, column)))]);
  // Typed cells keep the grid's display format (z) while staying numbers and dates.
  rows.forEach((row, r) => columns.forEach((column, c) => {
    const address = XLSX.utils.encode_cell({ r: r + 1, c });
    const typed = column.excel?.(row);
    if (typed && Number.isFinite(typed.v)) sheet[address] = { t: 'n', v: typed.v, z: typed.z, w: String(sheet[address]?.v ?? '') };
    const href = column.href?.(row);
    if (href && sheet[address]) sheet[address].l = { Target: href };
  }));
  sheet['!autofilter'] = { ref: sheet['!ref'] ?? 'A1' };
  sheet['!cols'] = columns.map((column) => ({ wch: Math.min(45, Math.max(18, column.label.length + 2)) }));
  XLSX.utils.book_append_sheet(book, sheet, 'Export');
  return new Blob([XLSX.write(book, { bookType: 'xlsx', type: 'array' })], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
